'use client';
import { createAuthClient } from 'better-auth/react';
import {
  organizationClient,
  twoFactorClient,
  magicLinkClient,
  inferAdditionalFields,
} from 'better-auth/client/plugins';
import { polarClient } from '@polar-sh/better-auth/client';
import { ac, roles } from './access-control-client';
import { env } from './env';

/**
 * Better Auth client mirror of backend/src/modules/auth/auth.config.ts.
 *
 * The organization client is given the same access-control (`ac`) and
 * `roles` as the backend so `authClient.organization.checkRolePermission`
 * resolves role-based UI gating locally. See lib/use-permission.tsx.
 *
 * NOTE: Passkey support requires the `@better-auth/passkey` companion
 * package which is not yet installed. The settings/login UI does NOT
 * expose passkey actions until that package is added on both ends.
 */
export const authClient = createAuthClient({
  baseURL: env.NEXT_PUBLIC_API_URL,
  fetchOptions: { credentials: 'include' },
  plugins: [
    twoFactorClient({
      onTwoFactorRedirect: () => {
        if (typeof window !== 'undefined') {
          window.location.href = '/two-factor';
        }
      },
    }),
    magicLinkClient(),
    organizationClient({ ac, roles }),
    inferAdditionalFields({
      user: {
        firstName: { type: 'string' },
        lastName: { type: 'string' },
      },
    }),
    // Polar billing: exposes authClient.checkout() and authClient.polar.portal().
    // polarClient() is optional on Free workspaces — it's a no-op when the
    // backend POLAR_API_KEY is not configured.
    polarClient(),
  ],
});

export const {
  signIn,
  signUp,
  signOut,
  useSession,
  twoFactor,
  organization,
  useActiveOrganization,
  useListOrganizations,
} = authClient;
